<template>
  <div :id="wrapperId" class="nation-map-wrapper">
    <loading class="map-loading-overlay" v-if="loading" color="#333" :loading="loading" text="Loading Models"></loading>
    <div :id="id" class="nationwide-map"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as d3 from 'd3'
import * as topojson from 'topojson'

import Utils from '@/utils'
import Loading from '@/components/common/Loading'

const name = 'nationwide_map'

const props = {
  id: {
    type: String,
    required: true
  },
  data: {
    type: Array,
    required: true
  },
  chamber: {
    type: String,
    required: true
  },
  loading: {
    type: Boolean,
    required: false
  }
}

const components = {
  Loading
}

const computed = {
  ...mapGetters({
    cds: 'geojson/cds',
    states: 'geojson/states',
    mobile: 'ux/mobile',
    screenWidth: 'ux/screenWidth'
  }),
  wrapperId () {
    return `${this.id}-wrapper`
  },
  senateSpecialElections () {
    return ['GA']
  },
  insetStatesList () {
    let states = ['NH', 'VT', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC']
    if (this.chamber === 'senate') {
      // add special elections here
      states = [...states, ...this.senateSpecialElections]
    }
    return states
  },
  insetStates () {
    return this.geojson.features
      .filter((item) => this.insetStatesList.includes(item.properties.STUSPS))
      .sort((a, b) => this.insetStatesList.indexOf(a.properties.STUSPS) - this.insetStatesList.indexOf(b.properties.STUSPS))
  }
}

const methods = {
  getGradient (perc) {
    return `linear-gradient(to right,
      var(--dem) 0%,
      var(--dem) ${perc}%,
      var(--gop) ${perc}%,
      var(--gop) 100%)`
  },
  getObject (d, isInset) {
    return this.data.find(item => {
      if (isInset && this.chamber === 'senate' && this.senateSpecialElections.includes(item.election.state)) {
        return item.election.geoid === d.properties.GEOID && item.election.senateClass === 'Special'
      } else if (isInset) {
        return item.election.geoid === d.properties.GEOID
        // return item.election.state === d.election.state && item.election.senateClass === 'Special'
      }
      if (item.election && this.chamber === 'house') {
        return item.election.geoid === d.properties.GEOID
      }
      if (item.election && ['senate', 'president'].includes(this.chamber)) {
        return item.election.geoid === d.properties.GEOID
      }
    })
  },
  getCandidates (obj) {
    // const electionJson = JSON.parse(obj.election.electionJson)
    const electionJson = obj.election.electionJson
    const candidates = Object.values(electionJson.currentElection)
    const dems = candidates.filter(item => item.party.includes('Democratic'))
    const reps = candidates.filter(item => item.party.includes('Republican') || item.party.includes('Conservative'))
    if (dems.length <= 1 || reps.length <= 1) {
      return [...dems, ...reps]
    } else {
      return 'Primary has not been decided yet'
    }
  },
  getDistrictState (value) {
    const obj = Utils.getColorScale(value)
    return `<span style="background-color: ${obj.color}; color: ${obj.textColor}; padding: 4px 8px;">${obj.state}</span>`
  },
  getPercentage (v) {
    let perc = v.toFixed(1)
    if (perc > 99) {
      perc = '> ' + 99
    } else if (perc < 1) {
      perc = '< ' + 1
    }
    return perc
  },
  getTooltipHtml (d, isInset) {
    const obj = this.getObject(d, isInset)
    if (!obj) {
      this.tooltip.style('display', 'none')
      return
    }
    const geoName = obj.election.raceName
    const candidates = this.getCandidates(obj)
    const resultData = [
      {
        party: 'Democratic',
        candidates: candidates.filter(c => c.party.includes('Democratic'))
          .map(c => c.name),
        winPerc: this.getPercentage(100 - (obj.gopWinProbability * 100)),
        sortWinPerc: 100 - (obj.gopWinProbability * 100),
        color: 'var(--dem)'
      },
      {
        party: 'Republican',
        candidates: candidates.filter(item => item.party.includes('Republican') || item.party.includes('Conservative'))
          .map(c => c.name),
        winPerc: this.getPercentage((obj.gopWinProbability * 100)),
        sortWinPerc: obj.gopWinProbability * 100,
        color: 'var(--gop)'
      }
    ]
    const tableRows = resultData
      .sort((a, b) => b.sortWinPerc - a.sortWinPerc)
      .map((c) => {
        let cands = ''
        c.candidates.forEach((cand, i) => {
          let name = i + 1 === c.candidates.length ? cand : `${cand}<br>`
          let span = `<span>${name}</span>`
          cands = cands + span
        })
        if (c.candidates.length === 0) {
          cands = 'No candidates'
        }
        const presidentialCandidates = {
          'Democratic': 'Biden',
          'Republican': 'Trump'
        }
        let rowTitle = this.chamber === 'president' ? presidentialCandidates[c.party] : c.party
        let candSpan = this.chamber !== 'president' ? `<span class="cand-list">${cands}</span>` : ''
        return `
          <tr class="candidates-table-row">
            <td class="tooltip-td" style="width: 6px; margin-left: 4px; background-color: ${c.color};"></td>
            <td class="tooltip-td" style="padding-left: 6px; text-align: left; padding-right: 5px;">
              <span style="font-weight: 700">${rowTitle}</span>
              <br>
              ${candSpan}
            </td>
            <td class="tooltip-td" style="text-align: right; padding-right: 5px; font-weight: 700; font-size: 16px !important;">
              ${c.winPerc}%
            </td>
          </tr>
        `
      }).join('')
    if (!tableRows) {
      this.tooltip.style('display', 'none')
      return
    }
    let header = `
      ${geoName}
      <span class="content-state">${this.getDistrictState(obj.rating)}</span>
    `
    if (this.smAndDown) {
      header = `
        <div class="content-title">
          ${header}
          <i class="tooltip-close material-icons" style="color: #333; font-size: 24px;">mdi-close</i>
        </div>
      `
    } else {
      header = `<div class="content-title">${header}</div>`
    }
    return `
      ${header}
      <table class="top-line-table" cellspacing="0">
        <tr class="candidates-table-headers">
          <th style="width: 8px; padding: 4px 0;"></th>
          <th style="text-align: left; padding-right: 6px; padding-left: 5px;"></th>
          <th style="text-align: right; padding-right: 5px;">Chance of winning</th>
        </tr>
      ${tableRows}
      </table>
    `
  },
  getTooltip (d, isInset) {
    const content = this.tooltip.append('div').attr('class', 'map-tooltip-content')
    content.html(() => this.getTooltipHtml(d, isInset))
  },
  initializeToolTip () {
    this.tooltip = d3
      .select('body')
      .append('div')
      .attr('class', 'map-tooltip')
      .on('click', () => {
        if (this.mobile && d3.event.target.className === 'tooltip-close material-icons') {
          this.tooltip.style('display', 'none')
        }
      })
      .style('display', 'none')
  },
  drawMap () {
    const self = this

    if (self.screenWidth < 500) {
      self.insetWidth = 20
    } else if (self.screenWidth < 600) {
      self.insetWidth = 24
    } else if (self.screenWidth < 700) {
      self.insetWidth = 28
    } else {
      self.insetWidth = 32
    }

    function onClick (d) {
      if (!self.mobile) {
        self.tooltip.style('display', 'none')
        const item = self.data.find(item => item.election.geoid === d.properties.GEOID)
        self.$router.push({ name: self.chamber, params: { raceId: item.election.raceId } })
      }
    }

    function onMouseOver (d, isInset) {
      if (!self.mobile) {
        self.tooltip.text('')
          .style('display', 'inline')
          .style('left', () => {
            return (d3.event.pageX - 300) + 'px'
          })
          .style('top', (d3.event.pageY - 150) + 'px')
      } else {
        self.tooltip.text('')
          .style('display', 'block')
      }

      self.getTooltip(d, isInset)
    }

    function onMouseMove () {
      if (!self.mobile) {
        const height = self.tooltip._groups[0][0].clientHeight + 12
        self.tooltip
          .style('left', () => {
            const pageWidth = document.body.clientWidth
            if (pageWidth - d3.event.pageX > 200) {
              return (d3.event.pageX - 100) + 'px'
            } else {
              return (d3.event.pageX - 200) + 'px'
            }
          })
          .style('top', (d3.event.pageY - height) + 'px')
      }
    }

    function onMouseOut () {
      self.tooltip.style('display', 'none')
    }

    const map = d3.select(`#${this.id}`)
    map.selectAll('svg').remove()
    const container = map.node()

    let width = map._groups[0][0].clientWidth
    let height

    const cds = JSON.parse(this.cds)
    const states = JSON.parse(this.states)

    if (this.chamber === 'house') {
      this.geojson = topojson.feature(cds, cds.objects.cd)
    } else {
      this.geojson = topojson.feature(states, states.objects.states)
    }

    const svg = d3.select(`#${this.id}`).append('svg')
      .attr('width', width)
      .attr('height', height)
      .style('position', 'absolute')
      .on('click', () => {
        if (!this.mobile) {
          this.tooltip.style('display', 'none')
        }
        if (d3.event.defaultPrevented) d3.event.stopPropagation()
      }, true)

    let projection = null
    let path = null

    projection = d3.geoAlbersUsa().scale(1).translate([0, 0])
    path = d3.geoPath().projection(projection)

    let b = path.bounds(self.geojson)
    b.height = Math.abs(b[1][1] - b[0][1])
    b.width = Math.abs(b[1][0] - b[0][0])
    let r = (b.height / b.width)
    let s = 0.9 / (b.width / width) // dimension of reference: width (constant)
    let t = [((width - s * (b[1][0] + b[0][0])) / 2) - self.insetWidth + 10, (width * r - s * (b[1][1] + b[0][1])) / 2] // translation

    projection
      .scale(s)
      .translate(t)
    svg.attr('height', width * r)

    const g = svg.append('g')

    self.initializeToolTip()

    g.selectAll('path')
      .data(this.geojson.features)
      .enter().append('path')
      .attr('class', 'cd')
      .attr('stroke', this.$vuetify.theme.currentTheme.background)
      .attr('d', path)
      .style('fill', (d) => {
        const obj = this.getObject(d)
        if (obj) {
          const scale = Utils.getColorScale(obj.rating)
          return obj ? scale.color : false
        } else {
          return 'rgba(102, 102, 102, 0.3)'
        }
      })
      .style('opacity', 0.9)
      .style('stroke-width', () => {
        if (this.chamber === 'house') {
          return '0.2px'
        } else {
          return '0.6px'
        }
      })
      .on('mouseover', (d) => onMouseOver(d))
      .on('mousemove', (d) => onMouseMove(d))
      .on('mouseout', (d) => onMouseOut(d))
      .on('click', (d) => onClick(d))

    if (self.insetStates.length > 0 && self.chamber !== 'house') {
      const spacing = 2
      let gInsets = svg.append('g')
      const svgHeight = svg._groups[0][0].height.baseVal.value

      const hasData = this.data.map(d => d.election.geoid)
      const insetStates = this.insetStates.filter(s => hasData.includes(s.properties.GEOID))

      const center = (svgHeight / 2) - ((insetStates.length * self.insetWidth) / 2)

      gInsets.selectAll('rect')
        .data(insetStates)
        .enter()
        .append('g')
        .attr('class', 'state-inset')
        .attr('transform', `translate(0, ${center})`)
        .style('alignment-baseline', 'central')
        .append('rect')
        .attr('width', self.insetWidth - spacing)
        .attr('height', self.insetWidth - spacing)
        .attr('x', `calc(100% - ${self.insetWidth - spacing}px)`)
        .attr('y', function (d, i) { return (i * self.insetWidth) })
        .attr('fill', function(d) {
          const obj = self.getObject(d, true)
          if (obj) {
            const scale = Utils.getColorScale(obj.rating)
            return obj ? scale.color : false
          } else {
            return 'rgba(102, 102, 102, 0.3)'
          }
        })
        .style('opacity', 0.9)
        .on('click', function (d) { onClick(d) })
        .on('mouseover', function (d) { onMouseOver(d, true) })
        .on('mousemove', function (d) { onMouseMove(d) })
        .on('mouseout', function (d) { onMouseOut(d) })

      gInsets.selectAll('.state-inset')
        .append('text')
        .attr('transform', function (d, i) {
          const x = container.clientWidth - (self.insetWidth / 2) - (spacing / 2) + 2
          const y = ((self.insetWidth) * i) + ((self.insetWidth) / 2) - (spacing / 2)
          return `translate(${x}, ${y})`
        })
        .style('text-anchor', 'middle')
        .style('alignment-baseline', 'central')
        .attr('fill', function (d) {
          const obj = self.getObject(d, true)
          if (obj) {
            const scale = Utils.getColorScale(obj.rating)
            return obj ? scale.textColor : false
          } else {
            return 'rgba(102, 102, 102, 0.3)'
          }
        })
        .style('font-size', (width * 0.0009) + 'em')
        .attr('font-weight', 700)
        .style('opacity', 0.8)
        .style('pointer-events', 'none')
        .text(function (d) {
          const obj = self.getObject(d, true)
          const multipleElections = self.data.filter(i => i.election.state === obj.election.state).length > 1
          if (obj.election.senateClass === 'Special' && multipleElections) {
            return obj.election.state + '*'
          } else {
            return obj.election.state
          }
        })

      const gLabels = svg.append('g')

      const nonInsetStates = this.geojson.features.filter((item) => {
        const hasData = this.data.map(d => d.election.geoid).includes(item.properties.GEOID)
        const special = this.chamber === 'senate' && this.senateSpecialElections.includes(item.properties.STUSPS)
        return !this.insetStatesList.includes(item.properties.STUSPS) && hasData || special
      })

      gLabels.selectAll('text')
        .data(nonInsetStates)
        .enter()
        .append('text')
        .text(function (d) { return d.properties.STUSPS })
        .attr('x', function (d) {
          if (path.centroid(d)[0]) {
            return path.centroid(d)[0]
          }
        })
        .attr('y', function (d) {
          if (path.centroid(d)[1]) {
            return path.centroid(d)[1]
          }
        })
        .attr('text-anchor', 'middle')
        .attr('dx', function (d) {
          const abb = d.properties.STUSPS
          if (self.labelAdjustments[abb] && self.labelAdjustments[abb].dx) {
            return self.labelAdjustments[abb].dx
          } else {
            return '0'
          }
        })
        .attr('dy', function (d) {
          const abb = d.properties.STUSPS
          if (self.labelAdjustments[abb] && self.labelAdjustments[abb].dy) {
            return self.labelAdjustments[abb].dy
          } else {
            return '0.35em'
          }
        })
        .style('fill', function (d) {
          if (d.properties.STUSPS === 'HI') {
            return '#222'
          }
          const obj = self.getObject(d)
          if (obj) {
            const scale = Utils.getColorScale(obj.rating)
            return obj ? scale.textColor : false
          } else {
            return 'rgba(102, 102, 102, 0.3)'
          }
          // return ['#bdbdbd', '#e0e0e0'].includes(self.getFillColor(d)) ? '#222' : '#fff'
        })
        .style('font-size', (width * 0.0009) + 'em')
        .style('font-weight', 700)
        .style('opacity', 0.8)
        .style('pointer-events', 'none')
    }
  }
}

export default {
  name,
  props,
  components,
  computed,
  methods,
  data () {
    return {
      blues: ['#dde9f0', '#c2d9e8', '#a5c9df', '#88bad7', 'var(--dem)'],
      reds: ['#fae4e4', '#ffbeb7', '#fe978d', '#f96d64', '#ef383d'],
      insetWidth: null,
      geojson: null,
      labelAdjustments: {
        'CA': { dx: '-1em' },
        'FL': { dx: '1.2em' },
        'ID': { dy: '1em' },
        'LA': { dx: '-0.9em', dy: '-0.7em' },
        'MI': { dx: '0.9em', dy: '2em' },
        'MN': { dx: '-0.3em' },
        'VA': { dx: '0.3em' },
        'WV': { dx: '-0.05em' }
      }
    }
  },
  mounted () {
    this.drawMap()
  },
  watch: {
    data () {
      this.drawMap()
    },
    screenWidth () {
      this.drawMap()
    },
    '$vuetify.theme.isDark'() {
      this.drawMap()
    }
  },
  beforeDestroy () {
    const els = document.querySelectorAll('.map-tooltip')
    els.forEach(el => el.remove())
  }
}
</script>

<style>
.nation-map-wrapper {
  position: relative;
  width: 100%;
}

.nationwide-map {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 500px;
  cursor: pointer;
}

.cd {
  fill: #9E9E9E;
  /* stroke: #fff; */
}
path:hover {
  fill-opacity: 0.7;
}

.map-tooltip {
  position: absolute;
  text-align: center;
  max-width: 280px;
  max-height: fit-content;
  padding: 10px;
  font: 12px sans-serif;
  background: var(--background-color);
  color: var(--text-color);
  border: 1px solid var(--text-color);
  pointer-events: none;
  z-index: 2000;
}

.map-tooltip-content {
  width: 100%;
  max-height: fit-content;
  padding-bottom: 2px;
}

.content-title {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.content-state {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-left: 5px;
}

.candidates-table-headers th {
  font-weight: 400;
  font-size: 11px;
  letter-spacing: 0.3px;
  color: var(--text-color);
  opacity: 0.7;
  border-bottom: 1px solid #666;
  padding-bottom: 3px;
}

.candidates-table-row td {
  border-bottom: 1.5px solid var(--border-color);
  padding-top: 5px;
  padding-bottom: 3px;
  font-size: 13px !important;
}

.cand-list {
  font-size: 11px;
  font-weight: 400;
  opacity: 0.8;
}

.top-line-table {
  width: 100%;
}

.map-loading-overlay {
  position: absolute;
  top: 40%;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
}

.map-loading-overlay p {
  font-weight: 600;
  color: #333;
  letter-spacing: 0.05em;
  padding: 0 5px;
}

/* TODO: do this in d3 instead */

@media only screen and (max-width: 1200px) {
  .nationwide-map {
    height: 400px;
  }
}

@media only screen and (max-width: 960px) {
  .nationwide-map {
    height: 580px;
  }
}

@media only screen and (max-width: 860px) {
  .nationwide-map {
    height: 470px;
  }
}

@media only screen and (max-width: 700px) {
  .nationwide-map {
    height: 400px;
  }
}

@media only screen and (max-width: 600px) {
  .nationwide-map {
    height: 350px;
  }
}

@media only screen and (max-width: 500px) {
  .nationwide-map {
    height: 300px;
  }
}

@media only screen and (max-width: 450px) {
  .nationwide-map {
    height: 250px;
  }
}

@media only screen and (max-width: 960px) {
  body {
    cursor: pointer;
  }

  .map-tooltip {
    position: fixed;
    bottom: 0px;
    min-width: 100%;
    max-width: 100%;
    max-height: 180px;
    z-index: 999;
    border: none;
    border-top: 1px solid var(--text-color);
    min-height: 80px;
    /* box-shadow: 0px -4px 3px var(--text-color) 10%; */
    pointer-events: all;
    cursor: pointer;
  }

  .map-tooltip-content {
    width: 100%;
    max-height: 140px;
    padding-bottom: 10px;
  }

  #tooltip-close {
    cursor: pointer;
  }
}
</style>
